import transparentBackground from "@/assets/images/transparent.jpg";
import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useGrammarResponseHandlers } from "@/lib/hooks/slides/editor/handlers/grammar/useGrammarResponseHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import {
  AIGradingModeEnum,
  type GrammarResponseSlideData,
  type VideoCheckpoint,
} from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { HiChevronDown, HiChevronRight, HiInformationCircle, HiX } from "react-icons/hi";
import { MdAccessTime, MdSkipNext } from "react-icons/md";
import { GradingModeInfoModal } from "./GradingModeInfoModal";
import { TimeInput } from "./TimeInput";

interface CheckpointBlockProps {
  checkpoint: VideoCheckpoint;
  index: number;
  isHighlighted?: boolean;
}

const GRADING_MODES: {
  value: VideoCheckpoint["gradingMode"];
  label: string;
  description: string;
  activeClass: string;
}[] = [
  {
    value: AIGradingModeEnum.Values.strictMode,
    label: "Strict",
    description: "Student must use the exact keyword from the expected answer.",
    activeClass: "bg-blue-500 text-white border-blue-500",
  },
  {
    value: AIGradingModeEnum.Values.contextMode,
    label: "Context",
    description: "Any contextually valid answer scores well. No expected answer needed.",
    activeClass: "bg-emerald-500 text-white border-emerald-500",
  },
  {
    value: AIGradingModeEnum.Values.grammarMode,
    label: "Grammar",
    description: "Student must follow the grammatical structure of the expected answer.",
    activeClass: "bg-purple-500 text-white border-purple-500",
  },
];

export function CheckpointBlock({
  checkpoint,
  index,
  isHighlighted = false,
}: CheckpointBlockProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const responseHandlers = useGrammarResponseHandlers(slide);
  const { handleFileDrop: handleImageFileDrop, isUploading: isUploadingImage } =
    useLocalImageUpload((media) => responseHandlers.addImage?.(media, String(index) ?? undefined));

  const dispatch = useAppDispatch();
  const slideData = useAppSelector(
    (state) => state.slideEditor.present.slide?.slideData as GrammarResponseSlideData | undefined
  );

  const twoCol = slideData?.mediaType === "audio";

  const otherTimestamps = slideData
    ? slideData.checkpoints.filter((_, i) => i !== index).map((c) => c.timestamp)
    : [];

  const updateCheckpoint = (updated: VideoCheckpoint) => {
    if (!slide || !slideData) return;
    const checkpoints = slideData.checkpoints
      .map((c, i) => (i === index ? updated : c))
      .sort((a, b) => a.timestamp - b.timestamp);
    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, checkpoints } }));
  };

  const removeCheckpoint = () => {
    if (!slide || !slideData) return;
    const checkpoints = slideData.checkpoints.filter((_, i) => i !== index);
    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, checkpoints } }));
  };

  const removeImage = () => {
    if (!slide) return;
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...slideData,
          checkpoints: slideData?.checkpoints.map((cp, i) =>
            i === index ? { ...cp, image: { src: null, id: null, filename: null } } : cp
          ),
        },
      })
    );
  };

  return (
    <div
      className={`flex flex-col rounded-lg border bg-surface shrink-0 transition-all duration-300 ${isHighlighted ? "border-amber-400 ring-2 ring-amber-300/50" : "border-border"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
            </IconButton>
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
              Checkpoint {index + 1}
            </span>
            <div className="flex items-center gap-1.5">
              <MdAccessTime className="text-muted-foreground/60 text-xs" />
              <TimeInput
                value={checkpoint.timestamp}
                otherTimestamps={otherTimestamps}
                onChange={(seconds) => updateCheckpoint({ ...checkpoint, timestamp: seconds })}
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={removeCheckpoint}
          aria-label="Remove checkpoint"
          className="shrink-0 p-1 rounded text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <HiX className="text-sm" />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-border pl-3 py-2">
        <IconButton size="sm">
          <MdSkipNext />
        </IconButton>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
          Skip to :
        </p>
        {checkpoint.skipTo != null ? (
          <div className="flex items-center gap-1">
            <TimeInput
              value={checkpoint.skipTo}
              otherTimestamps={[]}
              onChange={(seconds) => updateCheckpoint({ ...checkpoint, skipTo: seconds })}
            />
            <button
              type="button"
              onClick={() => updateCheckpoint({ ...checkpoint, skipTo: null })}
              className="text-muted-foreground/40 hover:text-red-400 transition-colors text-xs leading-none"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => updateCheckpoint({ ...checkpoint, skipTo: checkpoint.timestamp + 1 })}
            className="text-[9px] font-semibold text-muted-foreground/40 hover:text-primary-500 border border-dashed border-muted-foreground/30 hover:border-primary-400 px-1.5 py-0.5 rounded transition-colors"
          >
            + skip
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Grading mode */}
          <div className="flex flex-col gap-1 px-3 py-1.5 border-b border-border bg-gray-50/50">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground shrink-0">
                Grading :
              </span>
              <div className="flex gap-1">
                {GRADING_MODES.map((mode) => {
                  const isActive = checkpoint.gradingMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => updateCheckpoint({ ...checkpoint, gradingMode: mode.value })}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${
                        isActive
                          ? mode.activeClass
                          : "bg-surface text-muted-foreground border-border hover:border-gray-400"
                      }`}
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setIsInfoOpen(true)}
                className="text-muted-foreground/50 hover:text-blue-500 transition-colors"
              >
                <HiInformationCircle className="text-lg" />
              </button>
            </div>
            <p className="text-[9px] text-muted-foreground/70 leading-snug">
              {GRADING_MODES.find((m) => m.value === checkpoint.gradingMode)?.description}
            </p>
          </div>

          {/* Fields — image left / Q+A right on wide (audio) layout, stacked otherwise */}
          <div className={twoCol ? "flex gap-3 p-2" : "flex flex-col"}>
            {/* Image */}
            <DroppableZone
              id={`checkpoint-q-${String(index)}`}
              data={{ id: String(index), type: "reading-image" }}
              allowedType={["image"]}
              className={twoCol ? "rounded-xl shrink-0" : "rounded-xl"}
              onLocalFileDrop={handleImageFileDrop}
            >
              <div
                className={`relative rounded-md border border-gray-200 overflow-hidden aspect-[1/1] ${
                  twoCol ? "w-44" : "mx-auto my-2 w-full max-w-[200px]"
                }`}
              >
                {isUploadingImage ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-[10px] font-medium text-gray-400">Uploading…</p>
                  </div>
                ) : checkpoint.image?.src ? (
                  <>
                    <img
                      src={transparentBackground}
                      alt="background-transparent"
                      className="w-full h-full object-cover absolute z-0"
                    />
                    <img
                      src={checkpoint.image.src}
                      alt={checkpoint.image.filename || "image"}
                      className="w-full h-full object-cover absolute z-30"
                    />
                    <IconButton
                      variant="danger"
                      size="sm"
                      onClick={removeImage}
                      className="absolute right-1.5 top-1.5 p-0.5 z-50"
                    >
                      <HiX />
                    </IconButton>
                  </>
                ) : (
                  <div
                    // onClick={() => setActivePanel("Media")}
                    className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-center p-2"
                  >
                    <AiFillPicture className="text-violet-200 text-2xl" />
                    <p className="text-[10px] font-medium text-gray-400">
                      Drop image from media or local file here
                    </p>
                  </div>
                )}
              </div>
            </DroppableZone>

            <div className={twoCol ? "flex-1 min-w-0 flex flex-col gap-2" : "contents"}>
              {/* Question */}
              <div className="flex flex-col p-2 gap-1">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                  Question
                </label>
                <textarea
                  value={checkpoint.question || ""}
                  onChange={(e) => updateCheckpoint({ ...checkpoint, question: e.target.value })}
                  className="resize-none text-xs px-2 py-1.5 bg-surface border border-border rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none placeholder:text-muted-foreground/40 min-h-[48px]"
                  placeholder="What do you want for lunch ?"
                />
              </div>

              <GradingModeInfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />

              {/* Answer — hidden in context mode */}
              {checkpoint.gradingMode !== AIGradingModeEnum.Values.contextMode && (
                <div className="flex flex-col p-2 gap-1">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                    {checkpoint.gradingMode === AIGradingModeEnum.Values.grammarMode
                      ? "Grammar Template"
                      : "Answer"}
                  </label>
                  <textarea
                    value={checkpoint.answer || ""}
                    onChange={(e) => updateCheckpoint({ ...checkpoint, answer: e.target.value })}
                    className="resize-none text-xs px-2 py-1.5 bg-surface border border-border rounded focus:border-amber-400 focus:ring-1 focus:ring-amber-100 outline-none placeholder:text-muted-foreground/40 min-h-[48px]"
                    placeholder={
                      checkpoint.gradingMode === AIGradingModeEnum.Values.grammarMode
                        ? "I am going to ..."
                        : "spaghetti"
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
